"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@west-tennessee-consulting/ui";
import { Sparkles, RefreshCw, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShimmerLoader } from "@/components/ai/ai-summary/ShimmerLoader";
import { MarkdownComponents } from "@/components/ai/ai-summary/markdown-components";
import { parseStreamChunk } from "@/lib/utils/ai/stream-parser";
import { cn } from "@/lib/utils";
import { SummaryActions } from "@/components/ai/summary-actions";

// ============================================================================
// TICKET AI SUMMARY (on-demand)
// ============================================================================
// Streams an AI briefing for the ticket from /api/ai/ticket-summary. Never
// auto-runs — the user clicks Generate to get up to speed. Sits at the top of
// the ticket detail centerpiece so it's the first thing a triaging user sees.
// ============================================================================

interface TicketAISummaryProps {
  ticketId: string;
  className?: string;
}

const STATUS_MESSAGES = [
  "Reading the ticket and client context...",
  "Reviewing updates and logged time...",
  "Synthesizing a briefing...",
];

export const TicketAISummary = React.memo(function TicketAISummary({
  ticketId,
  className,
}: TicketAISummaryProps) {
  const { getAuthToken } = useAuth();
  const [content, setContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [statusMessage, setStatusMessage] = useState(STATUS_MESSAGES[0]);
  const abortRef = useRef<AbortController | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Unmount teardown. `abortRef` was declared and assigned but `.abort()` was
   * never called from anywhere, and the status `setInterval` was only cleared
   * on the stream's own `finally` — so navigating away from a ticket mid-brief
   * left the reader pulling chunks (retaining this whole card and its state
   * setters until the model finished) and the 3.5s timer ticking against a
   * component that no longer existed. Reading `.current` inside the cleanup is
   * the point: we want whatever is in flight AT unmount, not at mount.
   */
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
      if (statusTimerRef.current !== null) {
        clearInterval(statusTimerRef.current);
        statusTimerRef.current = null;
      }
    };
  }, []);

  const generate = useCallback(async () => {
    if (isStreaming) return;
    setError(null);
    setContent("");
    setCollapsed(false);
    setHasGenerated(true);
    setIsStreaming(true);
    setStatusMessage(STATUS_MESSAGES[0]);

    const controller = new AbortController();
    abortRef.current = controller;
    let tick = 0;
    const statusTimer = setInterval(() => {
      tick = Math.min(tick + 1, STATUS_MESSAGES.length - 1);
      setStatusMessage(STATUS_MESSAGES[tick]);
    }, 3500);
    statusTimerRef.current = statusTimer;

    try {
      const token = getAuthToken();
      const response = await fetch("/api/ai/ticket-summary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ticketId }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        let message = `Failed to generate summary (${response.status})`;
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(message);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const events = parseStreamChunk(decoder.decode(value, { stream: true }));
        for (const event of events) {
          if (typeof event.content === "string") {
            accumulated += event.content;
            setContent(accumulated);
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : "Failed to generate summary");
    } finally {
      clearInterval(statusTimer);
      statusTimerRef.current = null;
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [getAuthToken, isStreaming, ticketId]);

  const showGenerateCta = !hasGenerated && !isStreaming && !error;

  return (
    <Card className={cn("w-full border-blue-100 dark:border-blue-900/40", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-base md:text-lg">Ticket briefing</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {content && !isStreaming && (
              <SummaryActions
                title="Ticket briefing"
                markdown={content}
                entityType="tickets"
                entityId={ticketId}
              />
            )}
            {content && !isStreaming && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCollapsed((c) => !c)}
                title={collapsed ? "Expand" : "Collapse"}
              >
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </Button>
            )}
            {hasGenerated && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={generate}
                disabled={isStreaming}
                title="Regenerate"
              >
                <RefreshCw className={cn("h-4 w-4", isStreaming && "animate-spin")} />
              </Button>
            )}
          </div>
        </div>
        {showGenerateCta && (
          <p className="text-sm text-muted-foreground leading-snug">
            Get up to speed fast — an AI summary of this ticket, its history, logged time, attachments,
            and the client it&apos;s for.
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isStreaming && !content && <ShimmerLoader statusMessage={statusMessage} />}

        {content && !collapsed && <MarkdownComponents markdownContent={content} />}

        {content && collapsed && (
          <p className="text-sm text-muted-foreground italic">Briefing collapsed.</p>
        )}

        {showGenerateCta && (
          <Button type="button" onClick={generate} className="gap-2">
            <Sparkles className="h-4 w-4" />
            Generate AI summary
          </Button>
        )}

        {error && !isStreaming && (
          <Button type="button" variant="outline" onClick={generate} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        )}
      </CardContent>
    </Card>
  );
});

TicketAISummary.displayName = "TicketAISummary";
